import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Droplet,
  Plus,
  Search,
  RefreshCw,
  Trash2,
  Edit,
  Palette,
  ChevronDown,
  ChevronRight,
  Check,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common colour hex values for quick selection
const COMMON_COLOURS = {
  "Black": "#000000",
  "White": "#FFFFFF",
  "Navy": "#1B2A4E",
  "Grey Marle": "#9CA3AF",
  "Charcoal": "#374151",
  "Red": "#DC2626",
  "Royal": "#1D4ED8",
};

export default function SupplierColoursContent() {
  const [colours, setColours] = useState([]);
  const [bySupplier, setBySupplier] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingColour, setEditingColour] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    hex: "",
    supplier: "",
  });

  const getToken = () => localStorage.getItem("auth_token");

  useEffect(() => {
    loadColours();
    loadSuppliers();
  }, [selectedSupplier, showInactive]);

  const loadColours = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      if (selectedSupplier && selectedSupplier !== "all") {
        params.append("supplier", selectedSupplier);
      }
      params.append("active_only", showInactive ? "false" : "true");
      
      const res = await axios.get(`${API}/supplier-colours?${params}`, { headers });
      const data = res.data || {};
      setColours(data.colours || []);
      setBySupplier(data.by_supplier || {});
      
      // Auto-expand all suppliers
      const expanded = {};
      Object.keys(data.by_supplier || {}).forEach(s => expanded[s] = true);
      setExpandedSuppliers(expanded);
    } catch (error) {
      console.error("Failed to load colours", error);
      toast.error("Failed to load supplier colours");
    } finally {
      setIsLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/supplier-colours/suppliers`, { headers });
      setSuppliers(res.data.suppliers || []);
    } catch (error) {
      console.error("Failed to load suppliers", error);
    }
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error("Colour name is required");
      return;
    }
    if (!formData.supplier?.trim()) {
      toast.error("Supplier is required");
      return;
    }

    setIsSaving(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (editingColour) {
        await axios.put(`${API}/supplier-colours/${editingColour.id}`, formData, { headers });
        toast.success("Colour updated");
      } else {
        await axios.post(`${API}/supplier-colours`, formData, { headers });
        toast.success("Colour added");
      }

      setShowModal(false);
      setEditingColour(null);
      setFormData({ name: "", hex: "", supplier: "" });
      loadColours();
      loadSuppliers();
    } catch (error) {
      console.error("Failed to save colour", error);
      toast.error("Failed to save colour");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this colour?")) return;
    
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/supplier-colours/${id}`, { headers });
      toast.success("Colour deleted");
      loadColours();
    } catch (error) {
      toast.error("Failed to delete colour");
    }
  };

  const toggleActive = async (colour) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/supplier-colours/${colour.id}`, {
        ...colour,
        is_active: !colour.is_active,
      }, { headers });
      toast.success(colour.is_active ? "Colour deactivated" : "Colour activated");
      loadColours();
    } catch (error) {
      toast.error("Failed to update colour");
    }
  };

  const toggleSupplier = (supplier) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplier]: !prev[supplier],
    }));
  };

  const filteredBySupplier = Object.entries(bySupplier).filter(([supplier, colours]) => {
    if (searchQuery) {
      return colours.some(c => 
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        supplier.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return true;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Droplet className="w-5 h-5 text-cyan-600" />
            Supplier Colours
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage colour swatches by supplier for garment selection
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditingColour(null);
            setFormData({ name: "", hex: "", supplier: suppliers[0] || "" });
            setShowModal(true);
          }}
          className="h-9 text-xs bg-cyan-600 hover:bg-cyan-700"
        >
          <Plus className="w-3 h-3 mr-1.5" />
          Add Colour
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search colours..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm border-gray-200"
          />
        </div>
        <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
          <SelectTrigger className="w-48 h-9 text-sm">
            <SelectValue placeholder="All Suppliers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Suppliers</SelectItem>
            {suppliers.map(supplier => (
              <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch
            id="show-inactive"
            checked={showInactive}
            onCheckedChange={setShowInactive}
          />
          <Label htmlFor="show-inactive" className="text-xs text-gray-500">Show inactive</Label>
        </div>
      </div>

      {/* Colours by Supplier */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
          Loading colours...
        </div>
      ) : filteredBySupplier.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Palette className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">{searchQuery || selectedSupplier !== "all" ? "No colours found" : "No supplier colours yet"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBySupplier.map(([supplier, supplierColours]) => {
            const filteredColours = searchQuery 
              ? supplierColours.filter(c => c.name?.toLowerCase().includes(searchQuery.toLowerCase()))
              : supplierColours;
            
            if (filteredColours.length === 0) return null;
            
            return (
              <Collapsible
                key={supplier}
                open={expandedSuppliers[supplier]}
                onOpenChange={() => toggleSupplier(supplier)}
              >
                <CollapsibleTrigger className="w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {expandedSuppliers[supplier] ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="font-medium text-gray-900">{supplier}</span>
                    <Badge variant="secondary" className="text-xs">
                      {filteredColours.length} colours
                    </Badge>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="grid grid-cols-6 gap-2 p-3 pt-2">
                    {filteredColours.map((colour) => (
                      <div
                        key={colour.id}
                        className={`p-2 rounded-lg border transition-colors group ${
                          colour.is_active !== false 
                            ? "bg-white border-gray-100 hover:border-gray-200" 
                            : "bg-gray-50 border-gray-100 opacity-50"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className="w-6 h-6 rounded border border-gray-200 shadow-sm shrink-0"
                            style={{ backgroundColor: colour.hex || "#ccc" }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-900 truncate">{colour.name}</p>
                            {colour.hex && (
                              <p className="text-[10px] text-gray-500 font-mono">{colour.hex}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleActive(colour)}
                            className="h-6 w-6 p-0"
                            title={colour.is_active !== false ? "Deactivate" : "Activate"}
                          >
                            {colour.is_active !== false ? (
                              <X className="w-3 h-3 text-gray-400" />
                            ) : (
                              <Check className="w-3 h-3 text-green-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingColour(colour);
                              setFormData({
                                name: colour.name,
                                hex: colour.hex || "",
                                supplier: colour.supplier,
                              });
                              setShowModal(true);
                            }}
                            className="h-6 w-6 p-0"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(colour.id)}
                            className="h-6 w-6 p-0 text-red-500"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingColour ? "Edit Colour" : "Add Colour"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Supplier *</Label>
              <Select
                value={formData.supplier}
                onValueChange={(value) => setFormData({ ...formData, supplier: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Colour Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Navy Blue"
              />
            </div>
            <div className="space-y-2">
              <Label>Hex Code</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.hex}
                  onChange={(e) => setFormData({ ...formData, hex: e.target.value })}
                  placeholder="#000000"
                  className="flex-1"
                />
                <input
                  type="color"
                  value={formData.hex || "#000000"}
                  onChange={(e) => setFormData({ ...formData, hex: e.target.value })}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
              {/* Quick colour selection */}
              <div className="flex flex-wrap gap-1 mt-2">
                {Object.entries(COMMON_COLOURS).map(([name, hex]) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setFormData({ ...formData, hex, name: formData.name || name })}
                    className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
                    style={{ backgroundColor: hex }}
                    title={name}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
